import { useState, useEffect } from 'react'
import { useUserStore } from '../stores/userStore.js'

const API_BASE = import.meta.env.VITE_API_BASE ?? ''

const ROLES = ['owner','admin','moderator','vip','verified','member','new']
const ROLE_COLORS = {
  owner:'#F59E0B', admin:'#EF4444', moderator:'#8B5CF6',
  vip:'#38BDF8', verified:'#10B981', member:'#6B7A9E', new:'#3A4460'
}

export default function AdminPanel() {
  const { user, authHeader } = useUserStore()
  const [tab, setTab]         = useState('users')
  const [users, setUsers]     = useState([])
  const [storms, setStorms]   = useState([])
  const [loading, setLoading] = useState(false)
  const [msg, setMsg]         = useState(null)

  const [reports, setReports]   = useState([])
  const [modLog, setModLog]     = useState([])
  const [modUser, setModUser]   = useState(null)
  const [modHistory, setModHistory] = useState(null)
  const [reportFilter, setReportFilter] = useState('open')

  const isMod = ['owner','admin','moderator'].includes(user?.role)
  const isAdmin = ['owner','admin'].includes(user?.role)
  if (!isMod) return (
    <div style={{ padding:40, color:'var(--red)', fontFamily:'var(--mono)' }}>
      ⛔ Access denied — admins only
    </div>
  )

  useEffect(() => { if (tab === 'users') fetchUsers() }, [tab])
  useEffect(() => { if (tab === 'storms') fetchStorms() }, [tab])
  useEffect(() => { if (tab === 'reports') fetchReports() }, [tab, reportFilter])
  useEffect(() => { if (tab === 'modlog') fetchModLog() }, [tab])

  async function fetchUsers() {
    setLoading(true)
    try {
      const res  = await fetch(`${API_BASE}/users/list`, { headers: authHeader() })
      const data = await res.json()
      setUsers(data.users ?? [])
    } catch { setUsers([]) }
    setLoading(false)
  }

  async function fetchStorms() {
    setLoading(true)
    try {
      const res  = await fetch(`${API_BASE}/storms`, { headers: authHeader() })
      const data = await res.json()
      setStorms(data.storms ?? [])
    } catch { setStorms([]) }
    setLoading(false)
  }

  async function fetchReports() {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/moderation/reports?status=${reportFilter}`, { headers: authHeader() })
      const data = await res.json()
      setReports(data.reports ?? [])
    } catch { setReports([]) }
    setLoading(false)
  }

  async function fetchModLog() {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/moderation/log?limit=100`, { headers: authHeader() })
      const data = await res.json()
      setModLog(data.actions ?? [])
    } catch { setModLog([]) }
    setLoading(false)
  }

  async function resolveReport(id, status) {
    await fetch(`${API_BASE}/moderation/report/${id}`, {
      method:'PATCH', headers:{'Content-Type':'application/json',...authHeader()},
      body:JSON.stringify({ status }),
    })
    setMsg(`Report ${status}`)
    fetchReports()
  }

  async function lookupUser(userId) {
    try {
      const res = await fetch(`${API_BASE}/moderation/user/${userId}/history`, { headers: authHeader() })
      const data = await res.json()
      setModHistory(data)
    } catch { setModHistory(null) }
  }

  function timeAgo(ts) {
    if (!ts) return ''
    const s = Math.floor((Date.now() - ts) / 1000)
    if (s < 60) return `${s}s ago`
    if (s < 3600) return `${Math.floor(s/60)}m ago`
    if (s < 86400) return `${Math.floor(s/3600)}h ago`
    return `${Math.floor(s/86400)}d ago`
  }

  const ACTION_ICONS = {
    timeout:'⏱', warn:'⚠️', ban:'🚫', mute:'🔇',
    untimeout:'✅', unban:'✅', unmute:'✅',
    delete_message:'🗑', delete_post:'🗑', delete_reply:'🗑',
  }

  async function updateRole(userId, role) {
    const res = await fetch(`${API_BASE}/users/${userId}/role`, {
      method: 'PATCH',
      headers: { 'Content-Type':'application/json', ...authHeader() },
      body: JSON.stringify({ role }),
    })
    if (res.ok) { setMsg(`Role updated`); fetchUsers() }
  }

  async function deleteUser(userId, username) {
    if (!confirm(`Delete user ${username}? This cannot be undone.`)) return
    const res = await fetch(`${API_BASE}/users/${userId}`, {
      method: 'DELETE', headers: authHeader(),
    })
    if (res.ok) { setMsg(`User deleted`); fetchUsers() }
  }

  async function addStorm(e) {
    e.preventDefault()
    const fd = new FormData(e.target)
    const name = fd.get('name')?.trim()
    if (!name) return
    const id = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') + '-' + new Date().getFullYear()
    const body = { id, name, basin:'AL', category:'TD', status:'active', wind_kt:0, pressure_mb:0, lat:0, lon:0 }
    const res = await fetch(`${API_BASE}/storms`, {
      method: 'POST',
      headers: { 'Content-Type':'application/json', ...authHeader() },
      body: JSON.stringify(body),
    })
    if (res.ok) { setMsg('Storm room created'); e.target.reset(); fetchStorms() }
  }

  async function deleteStorm(id) {
    if (!confirm('Remove this storm?')) return
    await fetch(`${API_BASE}/storms/${id}`, { method:'DELETE', headers: authHeader() })
    setMsg('Storm removed'); fetchStorms()
  }

  return (
    <div style={{ height:'100%', overflowY:'auto', padding:24, fontFamily:'var(--sans)' }}>
      <div style={{ maxWidth:900, margin:'0 auto' }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
          <span style={{ fontSize:20 }}>⚡</span>
          <h1 style={{ fontSize:20, fontWeight:800, color:'var(--t1)', fontFamily:'var(--mono)' }}>
            CHASERNET ADMIN
          </h1>
          <span style={{ fontSize:11, color:'var(--t3)', fontFamily:'var(--mono)' }}>
            logged in as {user?.username}
          </span>
        </div>

        {msg && (
          <div onClick={() => setMsg(null)} style={{
            padding:'8px 14px', background:'rgba(16,185,129,0.12)',
            border:'1px solid rgba(16,185,129,0.3)', borderRadius:6,
            color:'var(--green)', fontSize:12, marginBottom:16, cursor:'pointer',
          }}>
            ✓ {msg} — click to dismiss
          </div>
        )}

        {/* Tabs */}
        <div style={{ display:'flex', gap:4, marginBottom:20, borderBottom:'1px solid var(--border)', paddingBottom:0, flexWrap:'wrap' }}>
          {['users','storms','reports','modlog','database'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding:'8px 16px', border:'none', background:'transparent',
              borderBottom:`2px solid ${tab===t ? 'var(--blue)' : 'transparent'}`,
              color: tab===t ? 'var(--blue)' : 'var(--t3)',
              fontWeight:700, fontSize:11, fontFamily:'var(--mono)',
              textTransform:'uppercase', letterSpacing:'0.06em', cursor:'pointer',
            }}>
              {t}
            </button>
          ))}
        </div>

        {/* ── USERS TAB ── */}
        {tab === 'users' && (
          <div>
            <div style={{ fontSize:11, color:'var(--t3)', fontFamily:'var(--mono)', marginBottom:12 }}>
              {users.length} users registered
            </div>
            {loading ? <div style={{ color:'var(--t3)' }}>Loading…</div> : (
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                {users.map(u => (
                  <div key={u.id} style={{
                    display:'flex', alignItems:'center', gap:12, padding:'10px 14px',
                    background:'var(--card)', borderRadius:8, border:'1px solid var(--border)',
                  }}>
                    <div style={{
                      width:32, height:32, borderRadius:'50%', flexShrink:0,
                      background:`${u.avatar_color ?? '#38BDF8'}18`,
                      border:`1.5px solid ${u.avatar_color ?? '#38BDF8'}`,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:11, fontWeight:700, color: u.avatar_color ?? '#38BDF8',
                      fontFamily:'var(--mono)',
                    }}>
                      {u.username[0].toUpperCase()}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'var(--t1)' }}>{u.username}</div>
                      <div style={{ fontSize:11, color:'var(--t3)' }}>{u.email}</div>
                    </div>
                    <select
                      value={u.role}
                      onChange={e => updateRole(u.id, e.target.value)}
                      style={{
                        background:'var(--panel)', border:`1px solid ${ROLE_COLORS[u.role] ?? 'var(--border)'}`,
                        borderRadius:5, color: ROLE_COLORS[u.role] ?? 'var(--t2)',
                        padding:'4px 8px', fontSize:11, fontFamily:'var(--mono)', fontWeight:700,
                      }}
                    >
                      {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                    {u.id !== user?.id && (
                      <button onClick={() => deleteUser(u.id, u.username)} style={{
                        padding:'4px 10px', background:'rgba(239,68,68,0.1)',
                        border:'1px solid rgba(239,68,68,0.3)', borderRadius:5,
                        color:'var(--red)', fontSize:11, fontFamily:'var(--mono)',
                      }}>
                        delete
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── STORMS TAB ── */}
        {tab === 'storms' && (
          <div>
            {/* Add storm form */}
            <div style={{ padding:16, background:'var(--card)', borderRadius:10, border:'1px solid var(--border)', marginBottom:20 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--amber)', fontFamily:'var(--mono)', marginBottom:12, letterSpacing:'0.08em' }}>
                + ADD STORM ROOM
              </div>
              <form onSubmit={addStorm}>
                <div style={{ marginBottom:10 }}>
                  <div style={{ fontSize:10, color:'var(--t3)', fontFamily:'var(--mono)', marginBottom:3 }}>Storm Name</div>
                  <input name="name" placeholder="e.g. Hurricane Milton" required style={{ width:'100%', padding:'8px 10px', boxSizing:'border-box' }} />
                </div>
                <button type="submit" style={{
                  padding:'8px 20px', background:'var(--amber)', border:'none',
                  borderRadius:6, color:'var(--bg)', fontWeight:700, fontSize:12,
                  fontFamily:'var(--mono)',
                }}>
                  CREATE STORM ROOM
                </button>
              </form>
            </div>

            {/* Storm list */}
            {loading ? <div style={{ color:'var(--t3)' }}>Loading…</div> : (
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                {storms.map(s => (
                  <div key={s.id} style={{
                    display:'flex', alignItems:'center', gap:12, padding:'10px 14px',
                    background:'var(--card)', borderRadius:8, border:'1px solid var(--border)',
                  }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'var(--t1)' }}>{s.name}</div>
                      <div style={{ fontSize:11, color:'var(--t3)', fontFamily:'var(--mono)' }}>
                        {s.category} · {s.wind_kt}kt · {s.basin} · {s.lat},{s.lon}
                      </div>
                    </div>
                    <div style={{
                      padding:'3px 8px', borderRadius:4, fontSize:10, fontFamily:'var(--mono)',
                      background: s.status === 'active' ? 'rgba(16,185,129,0.12)' : 'rgba(58,68,96,0.3)',
                      color: s.status === 'active' ? 'var(--green)' : 'var(--t3)',
                    }}>
                      {s.status === 'active' ? 'ACTIVE' : 'INACTIVE'}
                    </div>
                    <button onClick={() => deleteStorm(s.id)} style={{
                      padding:'4px 10px', background:'rgba(239,68,68,0.1)',
                      border:'1px solid rgba(239,68,68,0.3)', borderRadius:5,
                      color:'var(--red)', fontSize:11, fontFamily:'var(--mono)',
                    }}>
                      remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── REPORTS TAB ── */}
        {tab === 'reports' && (
          <div>
            <div style={{ display:'flex', gap:6, marginBottom:16 }}>
              {['open','reviewed','resolved','dismissed'].map(s => (
                <button key={s} onClick={() => setReportFilter(s)} style={{
                  padding:'5px 12px', borderRadius:5, border:'none', fontSize:10,
                  fontFamily:'var(--mono)', fontWeight:700, textTransform:'uppercase',
                  cursor:'pointer',
                  background: reportFilter===s ? 'rgba(56,189,248,0.15)' : 'var(--card)',
                  color: reportFilter===s ? 'var(--blue)' : 'var(--t3)',
                  border: `1px solid ${reportFilter===s ? 'rgba(56,189,248,0.3)' : 'var(--border)'}`,
                }}>{s}{s==='open' && reports.length > 0 ? ` (${reports.length})` : ''}</button>
              ))}
            </div>
            {loading ? <div style={{ color:'var(--t3)' }}>Loading…</div> : reports.length === 0 ? (
              <div style={{ padding:24, textAlign:'center', color:'var(--t3)', fontFamily:'var(--mono)', fontSize:12 }}>
                No {reportFilter} reports
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {reports.map(r => (
                  <div key={r.id} style={{
                    padding:'12px 14px', background:'var(--card)', borderRadius:8,
                    border:'1px solid var(--border)',
                  }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                      <div>
                        <span style={{ fontSize:12, fontWeight:700, color:'var(--t1)' }}>
                          {r.target_type === 'user' ? '👤' : '💬'} {r.target_type}
                        </span>
                        {r.target_username && (
                          <span style={{ fontSize:11, color:'var(--red)', fontFamily:'var(--mono)', marginLeft:6 }}>
                            @{r.target_username}
                          </span>
                        )}
                      </div>
                      <span style={{ fontSize:10, color:'var(--t3)', fontFamily:'var(--mono)' }}>
                        {timeAgo(r.created_at)}
                      </span>
                    </div>
                    <div style={{ fontSize:12, color:'var(--t2)', marginBottom:4 }}>
                      <strong>Reason:</strong> {r.reason}
                    </div>
                    {r.details && <div style={{ fontSize:11, color:'var(--t3)', marginBottom:4 }}>{r.details}</div>}
                    <div style={{ fontSize:10, color:'var(--t3)', fontFamily:'var(--mono)', marginBottom:8 }}>
                      Reported by: {r.reporter_username ?? 'unknown'}
                    </div>
                    {r.status === 'open' && (
                      <div style={{ display:'flex', gap:6 }}>
                        <button onClick={() => {
                          lookupUser(r.target_id)
                          setTab('modlog')
                        }} style={{
                          padding:'5px 12px', background:'rgba(56,189,248,0.1)',
                          border:'1px solid rgba(56,189,248,0.3)', borderRadius:5,
                          color:'var(--blue)', fontSize:10, fontFamily:'var(--mono)', cursor:'pointer',
                        }}>View User</button>
                        <button onClick={() => resolveReport(r.id, 'resolved')} style={{
                          padding:'5px 12px', background:'rgba(16,185,129,0.1)',
                          border:'1px solid rgba(16,185,129,0.3)', borderRadius:5,
                          color:'var(--green)', fontSize:10, fontFamily:'var(--mono)', cursor:'pointer',
                        }}>Resolve</button>
                        <button onClick={() => resolveReport(r.id, 'dismissed')} style={{
                          padding:'5px 12px', background:'rgba(239,68,68,0.1)',
                          border:'1px solid rgba(239,68,68,0.3)', borderRadius:5,
                          color:'var(--red)', fontSize:10, fontFamily:'var(--mono)', cursor:'pointer',
                        }}>Dismiss</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── MOD LOG TAB ── */}
        {tab === 'modlog' && (
          <div>
            {/* User lookup */}
            {modHistory && (
              <div style={{
                padding:16, background:'var(--card)', borderRadius:10,
                border:'1px solid var(--border)', marginBottom:20,
              }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{
                      width:36, height:36, borderRadius:'50%',
                      background:`${modHistory.user?.avatar_color ?? '#38BDF8'}18`,
                      border:`2px solid ${modHistory.user?.avatar_color ?? '#38BDF8'}`,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:13, fontWeight:700, color: modHistory.user?.avatar_color ?? '#38BDF8',
                      fontFamily:'var(--mono)',
                    }}>
                      {modHistory.user?.username?.[0]?.toUpperCase() ?? '?'}
                    </div>
                    <div>
                      <div style={{ fontSize:14, fontWeight:700, color:'var(--t1)' }}>{modHistory.user?.username}</div>
                      <div style={{ fontSize:10, color:'var(--t3)', fontFamily:'var(--mono)' }}>
                        {modHistory.user?.role} · joined {timeAgo(modHistory.user?.created_at)}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => setModHistory(null)} style={{
                    padding:'4px 10px', background:'var(--panel)', border:'1px solid var(--border)',
                    borderRadius:5, color:'var(--t3)', fontSize:10, cursor:'pointer',
                  }}>✕ Close</button>
                </div>

                {/* Active penalties */}
                {modHistory.penalties?.length > 0 && (
                  <div style={{ marginBottom:12 }}>
                    <div style={{ fontSize:10, color:'var(--red)', fontFamily:'var(--mono)', fontWeight:700, marginBottom:6 }}>
                      ACTIVE PENALTIES
                    </div>
                    {modHistory.penalties.map((p,i) => (
                      <div key={i} style={{
                        padding:'6px 10px', background:'rgba(239,68,68,0.08)',
                        border:'1px solid rgba(239,68,68,0.2)', borderRadius:5, marginBottom:4,
                        fontSize:11, color:'var(--t2)',
                      }}>
                        {ACTION_ICONS[p.type] ?? '•'} {p.type} — {p.reason || 'No reason'}
                        <span style={{ color:'var(--t3)', marginLeft:8 }}>
                          by {p.mod_username} · {p.expires_at ? `expires ${timeAgo(p.expires_at)}` : 'permanent'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Warnings */}
                {modHistory.warnings?.length > 0 && (
                  <div style={{ marginBottom:12 }}>
                    <div style={{ fontSize:10, color:'#F59E0B', fontFamily:'var(--mono)', fontWeight:700, marginBottom:6 }}>
                      WARNINGS ({modHistory.warnings.length})
                    </div>
                    {modHistory.warnings.map((w,i) => (
                      <div key={i} style={{ fontSize:11, color:'var(--t3)', padding:'3px 0' }}>
                        ⚠️ {w.reason} — <span style={{ fontFamily:'var(--mono)' }}>{w.mod_username} · {timeAgo(w.created_at)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Action history */}
                {modHistory.actions?.length > 0 && (
                  <div>
                    <div style={{ fontSize:10, color:'var(--t3)', fontFamily:'var(--mono)', fontWeight:700, marginBottom:6 }}>
                      HISTORY
                    </div>
                    {modHistory.actions.map((a,i) => (
                      <div key={i} style={{ fontSize:11, color:'var(--t3)', padding:'3px 0' }}>
                        {ACTION_ICONS[a.action] ?? '•'} {a.action} — {a.reason || 'No reason'}
                        <span style={{ fontFamily:'var(--mono)', marginLeft:6 }}>{a.mod_username} · {timeAgo(a.created_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Recent mod actions */}
            <div style={{ fontSize:11, color:'var(--t3)', fontFamily:'var(--mono)', marginBottom:12 }}>
              {modLog.length} recent actions
            </div>
            {loading ? <div style={{ color:'var(--t3)' }}>Loading…</div> : (
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                {modLog.map(a => (
                  <div key={a.id} style={{
                    display:'flex', alignItems:'center', gap:10, padding:'8px 12px',
                    background:'var(--card)', borderRadius:6, border:'1px solid var(--border)',
                  }}>
                    <span style={{ fontSize:16, width:24, textAlign:'center' }}>
                      {ACTION_ICONS[a.action] ?? '•'}
                    </span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, color:'var(--t1)' }}>
                        <strong>{a.mod_username}</strong>{' '}
                        <span style={{ color:'var(--t3)' }}>{a.action}</span>{' '}
                        {a.target_username && (
                          <span
                            onClick={() => lookupUser(a.target_id)}
                            style={{ color:'var(--blue)', cursor:'pointer', textDecoration:'underline' }}
                          >@{a.target_username}</span>
                        )}
                      </div>
                      {a.reason && <div style={{ fontSize:11, color:'var(--t3)', marginTop:2 }}>{a.reason}</div>}
                    </div>
                    <span style={{ fontSize:10, color:'var(--t3)', fontFamily:'var(--mono)', whiteSpace:'nowrap' }}>
                      {timeAgo(a.created_at)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── DATABASE TAB ── */}
        {tab === 'database' && (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {[
              { label:'D1 Database', val:'chasernet-db', note:'ec6ee472-7f7d-4d5f-aeeb-5740e3502cde' },
              { label:'KV Namespace', val:'chasernet-CHASERNET_KV', note:'1aee15e72bcf49cfa67255deb4a78707' },
              { label:'R2 Bucket', val:'chasernet-assets', note:'Object storage' },
              { label:'API Worker', val:'chasernet-api', note:'api.chasernet.com' },
              { label:'Cron Worker', val:'chasernet-cron', note:'Every 15 minutes' },
            ].map(r => (
              <div key={r.label} style={{ padding:'12px 16px', background:'var(--card)', borderRadius:8, border:'1px solid var(--border)' }}>
                <div style={{ fontSize:11, color:'var(--t3)', fontFamily:'var(--mono)', marginBottom:4 }}>{r.label}</div>
                <div style={{ fontSize:13, fontWeight:700, color:'var(--t1)', fontFamily:'var(--mono)' }}>{r.val}</div>
                <div style={{ fontSize:11, color:'var(--t2)', marginTop:2 }}>{r.note}</div>
              </div>
            ))}
            <div style={{ padding:14, background:'rgba(56,189,248,0.07)', border:'1px solid rgba(56,189,248,0.2)', borderRadius:8 }}>
              <div style={{ fontSize:11, color:'var(--blue)', fontFamily:'var(--mono)', marginBottom:6, fontWeight:700 }}>
                DIRECT DATABASE ACCESS
              </div>
              <div style={{ fontSize:12, color:'var(--t2)', lineHeight:1.6 }}>
                Go to <strong style={{ color:'var(--t1)' }}>dash.cloudflare.com → D1 → chasernet-db → Console</strong> to run SQL queries directly against your database.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
